import React from 'react';
import { Price } from 'ui/Price';
import { IBasketBuildL4 } from 'services/BackendApi/types/Basket';
import { getCurrencySymbol, formatPrice } from 'utils';
import { isOnHold } from './utils';
import { useCurrencyConversion } from 'context/CurrencyConversion/CurrencyConversionContext';
import { TCurrencyCode } from 'interfaces';
import { HidingTooltip } from 'ui/Tooltip';
import { SvgIcon } from 'ui/SvgIcon';
import InfoIcon from 'ui/Icons/tabler/info-circle.component.svg';

interface IBookingRightTotalCostProps {
  basketBuild: IBasketBuildL4;
  commissionAmount?: number;
  className?: string;
}

export const BasketRightTotalCost: React.FC<IBookingRightTotalCostProps> = React.memo(({ basketBuild, commissionAmount, className }) => {
  const effectiveBookingBuilder = isOnHold(basketBuild)
    ? basketBuild.initialBuildResponse
    : basketBuild?.latestBookingBuilderResponse;

  const { convert, displayCurrencySymbol, displayCurrencyCode, isConverting } = useCurrencyConversion();
  const currencySymbol = displayCurrencySymbol || getCurrencySymbol(effectiveBookingBuilder!.currency);
  const hotelCurrencySymbol = getCurrencySymbol(effectiveBookingBuilder!.currency);
  const hotelCurrency = effectiveBookingBuilder!.currency;

  const isOnRequest = effectiveBookingBuilder!.totals.oneOrMoreItemsOnRequest;
  const convertedTotal = isOnRequest ? 'On Request' : convert(effectiveBookingBuilder!.totals.total || 0);
  const convertedTotalBeforeDiscount = isOnRequest ? 'On Request' : convert(effectiveBookingBuilder!.totals.totalBeforeDiscount || 0);
  const convertedCommission = isOnRequest ? 0 : convert(parseFloat(String(commissionAmount) || '0'));

  const priceClassName = 'font-hurmegeometric-sans text-19px leading-20px font-semibold text-right mt-5px m-0';

  return (
    <React.Fragment>
      <div className="booking-right-total-cost bg-green-25 flex justify-between items-center min-h-[15px] p-[15px] pb-[5px]">
        <div className="flex flex-row items-center justify-end mt-[5px]">
          <p className="total-cost ml-1 font-hurmegeometric-sans text-[19px] leading-[23px] text-flint uppercase font-semibold m-0">
            Cost To You
          </p>
        </div>
        <div className="flex items-start gap-[4px]">
          {isConverting && (
            <HidingTooltip
              renderTooltipContent={() => (
                <div className="font-pt-sans text-13px p-2 max-w-[220px]">
                  This price is converted to show you the approximate cost in {displayCurrencyCode}. You'll pay in {hotelCurrency}. The exchange rate might change before you pay.
                </div>
              )}
              position="left"
              notRelativeClassName={true}
            >
              <SvgIcon IconComponent={InfoIcon} className="w-[16px] h-[16px] fill-gray-80 mt-[8px] cursor-pointer" />
            </HidingTooltip>
          )}
          <Price
            total={String(convertedTotal)}
            totalBeforeDiscount={String(convertedTotalBeforeDiscount)}
            currencyCode={(displayCurrencyCode || hotelCurrency) as TCurrencyCode}
            isOnRequest={effectiveBookingBuilder!.totals.oneOrMoreItemsOnRequest}
            totalClassName={`${priceClassName} text-black`}
            totalBeforeDiscountClassName={`font-hurmegeometric-sans text-15px leading-18px text-right mt-5px m-0 text-gray-80 line-through`}
            totalAfterDiscountClassName={`${priceClassName} text-red-15`}
            isOnRequestClassName={`${priceClassName} text-black`}
          />
        </div>
      </div>
      {isConverting && !effectiveBookingBuilder!.totals.oneOrMoreItemsOnRequest && (
        <div className="bg-green-25 flex justify-end items-center px-[15px] pb-[5px]">
          <span className="font-hurmegeometric-sans text-[13px] text-gray-100">
            Booking currency: {hotelCurrencySymbol}&nbsp;{formatPrice(effectiveBookingBuilder!.totals.total)}
          </span>
        </div>
      )}
      <div className="booking-right-total-cost bg-green-25 flex justify-between items-center min-h-[15px] p-[15px] pt-[0px]">
        <div className="flex flex-row items-center justify-end mt-[0px]">
          <p className="total-cost ml-1 font-hurmegeometric-sans text-[14px] leading-[23px] text-flint uppercase normal m-0">
            Commission
          </p>
        </div>
        <p className={`font-hurmegeometric-sans text-black text-[14px] font-normal`}>
          + {currencySymbol}&nbsp;{formatPrice(convertedCommission)}
        </p>
      </div>
    </React.Fragment>
  );
});
